import clsx from 'clsx';
import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react';
import { useIsDesktop } from '../../hooks/useMediaQuery';

export type Density = 'comfortable' | 'compact';
export type ColumnPriority = 'primary' | 'secondary';

export interface DataTableColumn<T> {
  key: string;
  label: string;
  /** Omit on every column to opt out of responsive collapsing entirely. */
  priority?: ColumnPriority;
  numeric?: boolean;
  render?: (row: T) => ReactNode;
}

export interface SelectionConfig<T> {
  isSelectable: (row: T) => boolean;
  isSelected: (row: T) => boolean;
  /** Receives the row ENTITY — callers store the entity, not the id, because
   *  a server-side status filter replaces the underlying list. */
  onToggleRow: (row: T) => void;
  onToggleAll: () => void;
  allSelected: boolean;
  someSelected: boolean;
  /** Accessible name for the select-all checkbox. */
  label?: string;
  /** Accessible name for a row checkbox. Defaults to `Select row ${rowKey(row)}`. */
  rowLabel?: (row: T) => string;
}

export interface DataTableProps<T> {
  data: T[];
  columns: DataTableColumn<T>[];
  rowKey: (row: T) => string;
  density?: Density;
  emptyState?: ReactNode;
  selection?: SelectionConfig<T>;
  rowActions?: (row: T) => ReactNode;
  /** Accessible name for the trailing row-actions column header. Rendered
   *  sr-only. Defaults to 'Actions', which is the literal text three of the
   *  four pre-migration hand-rolled tables used. Override where a screen has
   *  a more specific name for that column. */
  rowActionsLabel?: string;
  /** Opt-in: extra content per row, revealed by the row's expand toggle at
   *  EVERY width (not only below lg). Omit it and the table is unchanged. */
  renderExpanded?: (row: T) => ReactNode;
}

const CELL_PAD: Record<Density, string> = {
  comfortable: 'px-5 py-4',
  compact: 'px-3 py-2',
};

function cellContent<T>(col: DataTableColumn<T>, row: T): ReactNode {
  if (col.render) return col.render(row);
  return (row as Record<string, unknown>)[col.key] as ReactNode;
}

/**
 * Renders a real <table> at lg+. Below lg, if any column declares a priority,
 * only `primary` columns render and each row gets a toggle that reveals the
 * `secondary` columns — and `rowActions` — as a label/value list. If NO column
 * declares a priority the table falls back to a horizontal scroller — which is
 * what makes the component adoptable one screen at a time without breaking the
 * rest, and where `rowActions` stays a column because the scroller can reach
 * it.
 *
 * Callers keep the same `rowActions` contract at both widths: same callback,
 * same argument, same handlers. Only where the returned nodes are placed
 * changes. Note the one behavioural consequence for a screen whose actions are
 * navigation or a second disclosure: below lg they now cost an expand tap
 * first. If that matters for a given table, promote the action into a
 * `primary` column's `render` on that page rather than reopening this.
 */
export function DataTable<T>({
  data,
  columns,
  rowKey,
  density = 'comfortable',
  emptyState,
  selection,
  rowActions,
  rowActionsLabel = 'Actions',
  renderExpanded,
}: DataTableProps<T>) {
  const isDesktop = useIsDesktop();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const selectAllRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (selectAllRef.current && selection) {
      selectAllRef.current.indeterminate = selection.someSelected && !selection.allSelected;
    }
  }, [selection?.someSelected, selection?.allSelected]);

  const hasPriorities = columns.some((c) => c.priority !== undefined);
  const collapsing = !isDesktop && hasPriorities;

  const visible = collapsing ? columns.filter((c) => c.priority === 'primary') : columns;
  const hidden = collapsing ? columns.filter((c) => c.priority !== 'primary') : [];

  /**
   * `rowActions` was never part of the collapse budget. Below lg a row was
   * selection checkbox + 2 primary columns + a 44px toggle + an actions cell
   * rendering e.g. "Edit  Delete" side by side — which does not fit 375px, so
   * the actions clipped off the right edge (Tenants showed "Delet…",
   * MeterConfig "C…", TariffManagement lost them off-screen entirely). The
   * data collapsed; the actions didn't.
   *
   * So below lg the actions stop being a column and become the last entry of
   * the same disclosure the secondary columns already use. At lg+ they stay a
   * real column — `actionsAsColumn` is false ONLY while collapsing.
   *
   * `hasDetail` is deliberately `hidden.length > 0 || rowActions`, not just
   * `hidden.length > 0`: a table whose every column is `primary` but which
   * still supplies `rowActions` has nothing hidden, so under the old condition
   * it would render no toggle — and with the actions no longer in the row its
   * actions would be unreachable at every width below lg. The toggle is the
   * only affordance for anything a narrow row does not show, so it has to
   * appear whenever there is anything at all to reveal.
   */
  const actionsAsColumn = Boolean(rowActions) && !collapsing;
  const hasDetail = (collapsing && (hidden.length > 0 || Boolean(rowActions))) || Boolean(renderExpanded);
  // Below lg the actions live in the detail (they are not a column then); at
  // lg+ they stay a column, so the detail must not repeat them.
  const actionsInDetail = Boolean(rowActions) && !actionsAsColumn;
  const showDl = hidden.length > 0 || actionsInDetail;

  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  if (data.length === 0 && emptyState) {
    return <div className="py-12 text-center text-content-muted">{emptyState}</div>;
  }

  const pad = CELL_PAD[density];

  return (
    <div data-testid="datatable-scroller" className="overflow-x-auto">
      {/* overflow-x-auto applies in BOTH branches — the wrapper always needs
          to be able to scroll, or its own content, if it doesn't fit, pushes
          the whole page wider (see the git history of this comment for the
          375px incident that shipped when the collapsing branch got no
          containment at all). min-w-max, below, stays confined to the
          non-collapsing branch only:
          with plain `w-full` and the browser's auto table-layout, the table
          only grows past its container once the SUM of column min-content
          widths exceeds it — so narrow columns just word-wrap into slivers
          and `overflow-x-auto` on the parent never actually engages. Forcing
          the table to its natural max-content width makes it genuinely
          wider than the viewport when it needs to be, so the wrapper
          scrolls instead of squeezing. Left off the collapsing branch,
          which renders a label/value <dl>, not a wide table — it should
          keep sizing to its container; min-w-max there would force
          max-content width and defeat the collapse. */}
      <table className={clsx('w-full text-sm', !collapsing && 'min-w-max')}>
        <thead className="bg-surface-sunken">
          <tr>
            {selection ? (
              <th scope="col" className="w-12 pl-4">
                <label className="min-h-tap min-w-tap inline-flex items-center justify-center cursor-pointer">
                  <input
                    ref={selectAllRef}
                    type="checkbox"
                    checked={selection.allSelected}
                    onChange={selection.onToggleAll}
                    aria-label={selection.label ?? 'Select all rows'}
                    className="w-5 h-5 rounded border-border-token text-accent focus:ring-accent"
                  />
                </label>
              </th>
            ) : null}
            {visible.map((col) => (
              <th
                key={col.key}
                scope="col"
                className={clsx(
                  pad,
                  'text-xs font-semibold uppercase tracking-wide text-content-muted',
                  col.numeric ? 'text-right' : 'text-left',
                )}
              >
                {col.label}
              </th>
            ))}
            {/* The expand-toggle column's header, under the same "never a
                blank <th>" rule as the row-actions one below — it had been
                the last blank header in this component.

                "Row details", not "Details": this table's collapsing branch
                is adopted by screens that ALSO have a data column literally
                called "Details" (AuditLogs.tsx is one), and two columns with
                the same accessible name is the confusion an empty header was
                already causing, not a fix for it. Fixed rather than a prop
                like `rowActionsLabel`: the toggle column's meaning is the
                component's, identical on every screen, whereas the actions
                column holds caller-supplied content that screens name
                differently ("Actions", "Configure"). */}
            {hasDetail ? (
              <th scope="col" className="w-12">
                <span className="sr-only">Row details</span>
              </th>
            ) : null}
            {/* Never a blank <th>: a screen reader navigating this column
                announces the column header, and an empty one announces
                nothing. Pre-migration every hand-rolled table here carried
                visible header text ("Actions", or "TOU" on MeterConfig); the
                DataTable migration dropped it. sr-only rather than visible —
                the label is redundant to a sighted user (the cell content is
                self-describing: "View", "Edit / Delete", "Configure").

                Gated on `actionsAsColumn`, not `rowActions`: while collapsing
                the actions are not a column at all, so a trailing <th> here
                would leave the header row one cell wider than every body row.
                `rowActionsLabel` doesn't go unused there — it follows the
                actions into the detail block below, still sr-only, so the
                name a screen reader hears for that group is the same string
                at both widths. */}
            {actionsAsColumn ? (
              <th scope="col" className="w-20">
                <span className="sr-only">{rowActionsLabel}</span>
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody className="divide-y divide-border-token">
          {data.map((row) => {
            const key = rowKey(row);
            const isOpen = expanded.has(key);
            const isRowSelected = selection?.isSelected(row) ?? false;
            return (
              <Fragment key={key}>
                {/* `hover:bg-surface-sunken` (specificity 0,0,2,0) beats a plain
                    `bg-row-selected` (0,0,1,0) regardless of class order, so the
                    two must be mutually exclusive — otherwise mousing over a
                    selected row (exactly what happens while bulk-selecting down
                    a list) blanks out the only visual sign it's selected.
                    Selected rows intentionally get no extra hover treatment. */}
                <tr className={clsx(isRowSelected ? 'bg-row-selected' : 'hover:bg-surface-sunken')}>
                  {selection ? (
                    <td className="pl-4">
                      {selection.isSelectable(row) ? (
                        <label className="min-h-tap min-w-tap inline-flex items-center justify-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selection.isSelected(row)}
                            onChange={() => selection.onToggleRow(row)}
                            aria-label={selection.rowLabel?.(row) ?? `Select row ${rowKey(row)}`}
                            className="w-5 h-5 rounded border-border-token text-accent focus:ring-accent"
                          />
                        </label>
                      ) : null}
                    </td>
                  ) : null}
                  {visible.map((col) => (
                    <td
                      key={col.key}
                      className={clsx(
                        pad,
                        'text-content',
                        col.numeric && 'text-right',
                        col.numeric && 'tabular',
                      )}
                    >
                      {cellContent(col, row)}
                    </td>
                  ))}
                  {hasDetail ? (
                    <td className="pr-3">
                      <button
                        type="button"
                        aria-expanded={isOpen}
                        aria-controls={isOpen ? `row-detail-${key}` : undefined}
                        onClick={() => toggle(key)}
                        className="min-h-tap min-w-tap inline-flex items-center justify-center rounded-md text-content-muted hover:bg-surface-sunken"
                      >
                        <span className="sr-only">{isOpen ? 'Show less' : 'Show more'}</span>
                        <svg
                          className={clsx('w-4 h-4 transition-transform', isOpen && 'rotate-180')}
                          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    </td>
                  ) : null}
                  {actionsAsColumn ? (
                    <td className={clsx(pad, 'text-right')}>{rowActions?.(row)}</td>
                  ) : null}
                </tr>
                {hasDetail && isOpen ? (
                  <tr id={`row-detail-${key}`} className="bg-surface-sunken">
                    {/* colSpan: visible columns + the optional selection column
                        + the toggle column (always present here — `hasDetail`
                        is what put this row on screen) + the actions column
                        ONLY when it exists, which is lg+ (`actionsAsColumn`).
                        While collapsing it doesn't exist, and over-spanning
                        would push a phantom column's width onto every row. */}
                    <td
                      colSpan={visible.length + 1 + (selection ? 1 : 0) + (actionsAsColumn ? 1 : 0)}
                      className="px-5 pb-4 pt-1"
                    >
                      {showDl ? (
                        <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
                          {hidden.map((col) => (
                            <div key={col.key} className="contents">
                              <dt className="text-xs text-content-muted">{col.label}</dt>
                              <dd className={clsx('text-sm text-content text-right', col.numeric && 'tabular')}>
                                {cellContent(col, row)}
                              </dd>
                            </div>
                          ))}
                          {/* The actions, evicted from the row. `col-span-2` so
                              they get the full detail width instead of the
                              half a <dd> gets — that width is the whole point,
                              it's what stopped "Edit  Delete" clipping. `flex-wrap`
                              because callers pass an arbitrary number of
                              controls and each one carries the 44px tap floor
                              below lg (their `lg:min-h-0` escape only bites at
                              lg, where this block never renders).

                              `border-t` only when something sits above it, and
                              it is DECORATION, not the grouping signal: computed
                              from hex, --border on --surface-sunken is 1.19:1
                              light (#e7e5e4 on #faf9f7) and 1.92:1 dark (#44403c
                              on #0c0a09) — under the 3:1 non-text floor. It's
                              the same token the table's own row dividers use, so
                              raising it here alone would just look wrong; what
                              actually separates the group is the `mt-1 pt-3`
                              spacing, which stands on its own if the hairline is
                              invisible. Introduces no new TEXT pairing: the
                              sr-only <dt> renders nothing, and the actions carry
                              the caller's own colours. */}
                          {actionsInDetail && rowActions ? (
                            <div
                              className={clsx(
                                'col-span-2',
                                hidden.length > 0 && 'mt-1 border-t border-border-token pt-3',
                              )}
                            >
                              <dt className="sr-only">{rowActionsLabel}</dt>
                              <dd className="flex flex-wrap items-center justify-end gap-x-3 gap-y-2">
                                {rowActions(row)}
                              </dd>
                            </div>
                          ) : null}
                        </dl>
                      ) : null}
                      {renderExpanded ? (
                        <div className={clsx(showDl && 'mt-3 border-t border-border-token pt-3')}>
                          {/* w-0 min-w-full: the content contributes no width to
                              the table's max-content size (lg+ is min-w-max), so
                              a long line can never widen the table; it fills the
                              cell instead. */}
                          <div className="w-0 min-w-full">{renderExpanded(row)}</div>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
